import type { ColorSystemOptions } from '@mui/material/styles';

import { themeConfig } from './theme-config';
import { createPaletteChannel, varAlpha } from './utils';

export type PaletteColorExtend = {
  lighter: string;
  darker: string;
  lighterChannel: string;
  darkerChannel: string;
};

export type GreyExtend = Record<
  | '50Channel'
  | '100Channel'
  | '200Channel'
  | '300Channel'
  | '400Channel'
  | '500Channel'
  | '600Channel'
  | '700Channel'
  | '800Channel'
  | '900Channel',
  string
>;

export type CommonColorsExtend = { whiteChannel: string; blackChannel: string };
export type TypeTextExtend = { disabledChannel: string };
export type TypeBackgroundExtend = { neutral: string; neutralChannel: string };

export const primary = createPaletteChannel(themeConfig.palette.primary);
export const secondary = createPaletteChannel(themeConfig.palette.secondary);
export const info = createPaletteChannel(themeConfig.palette.info);
export const success = createPaletteChannel(themeConfig.palette.success);
export const warning = createPaletteChannel(themeConfig.palette.warning);
export const error = createPaletteChannel(themeConfig.palette.error);
export const common = createPaletteChannel(themeConfig.palette.common);
export const grey = createPaletteChannel(themeConfig.palette.grey);

const shared = { primary, secondary, info, success, warning, error, common, grey };

function createAction(activeColor: string) {
  return {
    hover: varAlpha(grey['500Channel'], 0.08),
    selected: varAlpha(grey['500Channel'], 0.16),
    focus: varAlpha(grey['500Channel'], 0.24),
    disabled: varAlpha(grey['500Channel'], 0.8),
    disabledBackground: varAlpha(grey['500Channel'], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
    active: activeColor,
  };
}

/** Tinta sobre papel: o par de 18:1 do guia. */
export const lightPalette: ColorSystemOptions['palette'] = {
  ...shared,
  divider: varAlpha(grey['500Channel'], 0.2),
  text: createPaletteChannel({
    primary: themeConfig.brand.ink,
    secondary: grey[600],
    disabled: grey[500],
  }),
  background: createPaletteChannel({
    paper: themeConfig.brand.paper,
    default: themeConfig.brand.paper,
    neutral: grey[100],
  }),
  action: createAction(grey[600]),
};

/**
 * Papel sobre tinta: as superfícies escuras que o guia prevê para a marca, com o D em
 * branco. O `paper` sobe um tom para cartões e cabeçalho se destacarem do fundo.
 */
export const darkPalette: ColorSystemOptions['palette'] = {
  ...shared,
  divider: varAlpha(grey['500Channel'], 0.2),
  text: createPaletteChannel({
    primary: themeConfig.brand.paper,
    secondary: grey[500],
    disabled: grey[600],
  }),
  background: {
    paper: grey[800],
    paperChannel: grey['800Channel'],
    default: grey[900],
    defaultChannel: grey['900Channel'],
    neutral: varAlpha(grey['500Channel'], 0.12),
    neutralChannel: grey['500Channel'],
  },
  action: createAction(grey[500]),
};
